import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { API_BASE_URL } from '../utils/api';

const DEFAULT_AVATAR_URL = 'https://res.cloudinary.com/djba6ta1n/image/upload/v1753003808/avatars/author.default.profile_dgfkvu.jpg'; // Replace with your own default avatar if needed

export default function RegisterForm({ onRegister, onSwitchToLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accountName, setAccountName] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [githubLoading, setGithubLoading] = useState(false);
  const [error, setError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password || !accountName) {
      setError('All fields are required.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (!acceptedTerms || !acceptedPrivacy) {
      setError('You must agree to the Terms and Privacy Policy to register.');
      return;
    }
    setLoading(true);
    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { account_name: accountName, has_accepted_terms: true, has_accepted_privacy: true }
      }
    });
    setLoading(false);
    if (signUpError) {
      setError(signUpError.message);
    } else {
      // Patch: After registration, update profile with default avatar and name
      try {
        const session = data.session || (await supabase.auth.getSession()).data.session;
        const token = session?.access_token;
        if (token) {
          await fetch(`${API_BASE_URL}/api/profile/update`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              avatar_url: DEFAULT_AVATAR_URL,
              name: accountName,
              has_accepted_terms: true
            })
          });
        }
      } catch (err) {
        // Optionally handle error
      }
      onRegister && onRegister(data);
    }
  };

  const signInWithGoogle = async () => {
    setGoogleLoading(true);
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
    });
    if (error) {
      setError(error.message);
    } else {
      onRegister && onRegister(data);
    }
    setGoogleLoading(false);
  };

  const signInWithGitHub = async () => {
    setGithubLoading(true);
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
    });
    if (error) {
      setError(error.message);
    } else {
      onRegister && onRegister(data);
    }
    setGithubLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <h2 className="text-xl font-bold mb-2 text-center">Register</h2>
      <input
        type="email"
        className="rounded px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
        placeholder="Email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        autoComplete="email"
        required
      />
      <input
        type="password"
        className="rounded px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
        placeholder="Password (min 6 chars)"
        value={password}
        onChange={e => setPassword(e.target.value)}
        autoComplete="new-password"
        required
      />
      <input
        type="text"
        className="rounded px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
        placeholder="Account Name"
        value={accountName}
        onChange={e => setAccountName(e.target.value)}
        required
      />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={acceptedTerms}
          onChange={e => setAcceptedTerms(e.target.checked)}
          required
        />
        I agree to the
        <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Terms of Service</a>
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={acceptedPrivacy}
          onChange={e => setAcceptedPrivacy(e.target.checked)}
          required
        />
        I agree to the
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a>
      </label>
      {error && <div className="text-red-600 text-sm text-center">{error}</div>}
      <button
        type="submit"
        className="bg-blue-600 text-white rounded py-2 font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60"
        disabled={loading || !acceptedTerms || !acceptedPrivacy}
      >
        {loading ? 'Registering...' : 'Register'}
      </button>
      <div className="relative flex py-2 items-center">
        <div className="flex-grow border-t border-gray-300"></div>
        <span className="flex-shrink mx-4 text-gray-500">Or</span>
        <div className="flex-grow border-t border-gray-300"></div>
      </div>
      <button
        type="button"
        onClick={signInWithGoogle}
        className="bg-white border border-gray-300 text-gray-700 rounded py-2 font-semibold hover:bg-gray-50 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        disabled={googleLoading}
      >
        <svg className="w-5 h-5" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M44.5 24c0-1.52-.14-2.98-.4-4.39H24v8.51h11.3c-.49 2.76-1.93 5.1-4.01 6.62v5.5h7.08c4.14-3.82 6.56-9.47 6.56-16.24z" fill="#4285F4"/>
          <path d="M24 45c6.48 0 11.93-2.13 15.89-5.81l-7.08-5.5c-2.15 1.45-4.92 2.3-8.81 2.3-6.77 0-12.5-4.55-14.56-10.68H2.3v5.68C6.24 40.45 14.48 45 24 45z" fill="#34A853"/>
          <path d="M9.44 26.38c-.38-1.13-.6-2.33-.6-3.58s.22-2.45.6-3.58V13.5H2.3C.88 16.37 0 19.99 0 24s.88 7.63 2.3 10.5l7.14-5.12z" fill="#FBBC05"/>
          <path d="M24 8.99c3.45 0 6.55 1.19 9 3.67l6.27-6.27C35.93 2.85 30.48 0 24 0 14.48 0 6.24 4.55 2.3 11.82l7.14 5.68c2.06-6.13 7.79-10.68 14.56-10.68z" fill="#EA4335"/>
        </svg>
        Sign up with Google
      </button>
      <button
        type="button"
        onClick={signInWithGitHub}
        className="bg-gray-800 text-white rounded py-2 font-semibold hover:bg-gray-900 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        disabled={githubLoading}
      >
        <svg className="w-5 h-5" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" clipRule="evenodd"/>
        </svg>
        Sign up with GitHub
      </button>
      {onSwitchToLogin && (
        <div className="text-center text-sm mt-2">
          Already have an account?{' '}
          <button type="button" className="text-blue-600 hover:underline" onClick={onSwitchToLogin}>
            Login
          </button>
        </div>
      )}
    </form>
  );
} 
